import { type ReactNode } from "react";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { HStack } from "@design-system/HStack/HStack";
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "@design-system/ResizablePanels/ResizablePanels";
import { VStack } from "@design-system/VStack/VStack";
import {
    StandardModalBody,
    StandardModalFooter,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { HOTKEYS_SCOPES } from "@hooks/keyboard/useKeyboardCommand";
import { useScopeSwitch } from "@hooks/keyboard/useScopeSwitch";
import { FullScreenExitIcon, FullScreenIcon } from "@icons";
import drawerStyles from "../ScheduleDrawer.module.scss";
import styles from "./ScheduleManualModal.module.scss";

interface ScheduleManualModalFrameProps {
    title: string;
    subtitle: string;
    isExpanded: boolean;
    onExpandedChange: (isExpanded: boolean) => void;
    preview: ReactNode;
    children: ReactNode;
    secondaryLabel: string;
    onSecondary: () => void;
    /** Replaces the secondary button in full screen with one that collapses back to the modal. */
    expandedSecondaryLabel?: string;
    submitLabel: string;
    isSubmitDisabled: boolean;
    isSubmitting: boolean;
    onSubmit: () => void;
}

/**
 * The header, body and footer of the manual schedule modal, rendered inside a
 * standard `Modal` that the owner flips to `fullscreen` when expanded.
 */
export const ScheduleManualModalFrame = ({
    title,
    subtitle,
    isExpanded,
    onExpandedChange,
    preview,
    children,
    secondaryLabel,
    onSecondary,
    expandedSecondaryLabel,
    submitLabel,
    isSubmitDisabled,
    isSubmitting,
    onSubmit,
}: ScheduleManualModalFrameProps) => {
    useScopeSwitch(HOTKEYS_SCOPES.GENERAL_MODAL);
    const expandLabel = isExpanded ? "Exit full screen" : "Full screen";
    const isBackShown = isExpanded && Boolean(expandedSecondaryLabel);
    return (
        <>
            <StandardModalHeader
                className={styles.header}
                data-expanded={isExpanded}
            >
                {isExpanded ? (
                    <h2 className={styles.title}>{title}</h2>
                ) : (
                    <VStack gap={4}>
                        <h2 className={styles.title}>{title}</h2>
                        <p className={styles.subtitle}>{subtitle}</p>
                    </VStack>
                )}
                <Tooltip
                    key={isExpanded ? "exit" : "enter"}
                    content={expandLabel}
                >
                    <Button
                        className={styles.expandButton}
                        variant="secondary"
                        size="md"
                        iconOnly
                        aria-label={expandLabel}
                        aria-pressed={isExpanded}
                        onClick={() => onExpandedChange(!isExpanded)}
                    >
                        {isExpanded ? (
                            <FullScreenExitIcon />
                        ) : (
                            <FullScreenIcon />
                        )}
                    </Button>
                </Tooltip>
            </StandardModalHeader>
            <StandardModalBody
                className={styles.body}
                data-expanded={isExpanded}
            >
                {isExpanded ? (
                    <ResizablePanelGroup
                        orientation="horizontal"
                        className={styles.split}
                    >
                        <ResizablePanel
                            id="schedule-modal-form-panel"
                            defaultSize="50%"
                            minSize="30%"
                        >
                            <div className={styles.formColumn}>{children}</div>
                        </ResizablePanel>
                        <ResizableHandle
                            className={drawerStyles.resizeHandle}
                        />
                        <ResizablePanel
                            id="schedule-modal-preview-panel"
                            defaultSize="50%"
                            minSize="30%"
                        >
                            <div className={styles.preview}>{preview}</div>
                        </ResizablePanel>
                    </ResizablePanelGroup>
                ) : (
                    children
                )}
            </StandardModalBody>
            <StandardModalFooter>
                <HStack gap={8} align="center">
                    <Button
                        type="button"
                        variant="ghost"
                        size="md"
                        onClick={
                            isBackShown
                                ? () => onExpandedChange(false)
                                : onSecondary
                        }
                    >
                        {isBackShown ? expandedSecondaryLabel : secondaryLabel}
                    </Button>
                    <ButtonWithLoader
                        variant="primary"
                        size="md"
                        loading={isSubmitting}
                        disabled={isSubmitDisabled}
                        onClick={onSubmit}
                        hotKey="mod+enter"
                        hotKeyOptions={{
                            enabled: !isSubmitDisabled && !isSubmitting,
                            description: submitLabel,
                            preventDefault: true,
                            enableOnFormTags: true,
                            scopes: [HOTKEYS_SCOPES.GENERAL_MODAL],
                        }}
                        hotKeyDisplayPosition="inline"
                    >
                        {submitLabel}
                    </ButtonWithLoader>
                </HStack>
            </StandardModalFooter>
        </>
    );
};
